import React, { useState, useEffect } from 'react';
import './i18n/index.js';
import { useTranslation } from 'react-i18next';
import FilterBar from './components/FilterBar';
import ActivityCard from './components/ActivityCard';
import DetailSheet from './components/DetailSheet';
import MapView from './components/MapView';
import { supabase } from './lib/supabase';
import { AMBER, applyFilters, sortByDistance } from './lib/data';

const globalStyles = `
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Segoe UI', system-ui, sans-serif; background: #FDF8F3; overflow: hidden; }
  @keyframes cardIn { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:translateY(0); } }
  @keyframes bannerFadeOut { from { opacity:1; max-height:120px; } to { opacity:0; max-height:0; } }
  .scrollbar-hide::-webkit-scrollbar { display: none; }
  .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
  .sheet-open .leaflet-control-container { display: none; }
`;

const INPUT_STYLE = {
  width: '100%', padding: '12px 14px', borderRadius: 12,
  border: '1px solid #e5e5e5', fontSize: 14, fontFamily: 'inherit',
  background: '#fff', outline: 'none', color: '#111',
};

const LABEL_STYLE = {
  fontSize: 12, fontWeight: 700, color: '#888',
  textTransform: 'uppercase', letterSpacing: .5,
  display: 'block', marginBottom: 6,
};

const FORMSPREE_SUGGEST = 'mgopoknq';
const FORMSPREE_CONTACT = 'meepegrv';

// ─── Welcome Banner ───────────────────────────────────────────────────────────
function WelcomeBanner({ onDismiss }) {
  const [hiding, setHiding] = useState(false);

  function handleDismiss() {
    setHiding(true);
    setTimeout(onDismiss, 350);
  }

  return (
    <div style={{ overflow: 'hidden', animation: hiding ? 'bannerFadeOut 0.35s ease forwards' : 'none', flexShrink: 0 }}>
      <div style={{ margin: '8px 12px', background: '#FFF3E0', border: '1px solid #FFE0B2', borderRadius: 14, padding: '11px 12px', display: 'flex', alignItems: 'center', gap: 10, boxShadow: '0 2px 8px rgba(245,158,11,.12)' }}>
        <span style={{ fontSize: 26, flexShrink: 0 }}>🗺️</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 800, fontSize: 13, color: '#92400E', marginBottom: 2 }}>Witaj w KidzMap!</div>
          <div style={{ fontSize: 12, color: '#B45309', lineHeight: 1.4 }}>Odkryj atrakcje dla dzieci w Warszawie. Użyj filtrów, żeby dopasować do wieku i budżetu.</div>
        </div>
        <button onClick={handleDismiss} style={{ flexShrink: 0, width: 28, height: 28, borderRadius: 99, border: 'none', background: 'rgba(180,83,9,.12)', color: '#92400E', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 700 }}>×</button>
      </div>
    </div>
  );
}

// ─── Form component ───────────────────────────────────────────────────────────
function ContactForm({ endpoint, fields, submitLabel }) {
  const [values, setValues] = useState({});
  const [status, setStatus] = useState('idle');

  function handleChange(e) {
    setValues(v => ({ ...v, [e.target.name]: e.target.value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setStatus('sending');
    try {
      const res = await fetch(`https://formspree.io/f/${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(values),
      });
      setStatus(res.ok ? 'success' : 'error');
    } catch {
      setStatus('error');
    }
  }

  if (status === 'success') {
    return (
      <div style={{ background: '#F0FFF4', border: '1px solid #BBF7D0', borderRadius: 12, padding: '16px', textAlign: 'center' }}>
        <div style={{ fontSize: 24, marginBottom: 8 }}>✅</div>
        <div style={{ fontWeight: 700, color: '#166534', fontSize: 14 }}>Dziękujemy! Odezwiemy się wkrótce.</div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {fields.map(f => (
        <div key={f.name}>
          <label style={LABEL_STYLE}>{f.label}{f.required && ' *'}</label>
          {f.type === 'textarea' ? (
            <textarea name={f.name} required={f.required} placeholder={f.placeholder} onChange={handleChange}
              style={{ ...INPUT_STYLE, minHeight: 90, resize: 'vertical' }} />
          ) : f.type === 'select' ? (
            <select name={f.name} required={f.required} onChange={handleChange} style={INPUT_STYLE}>
              <option value="">Wybierz kategorię…</option>
              {f.options.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          ) : (
            <input type={f.type} name={f.name} required={f.required} placeholder={f.placeholder} onChange={handleChange} style={INPUT_STYLE} />
          )}
        </div>
      ))}
      {status === 'error' && (
        <div style={{ color: '#DC2626', fontSize: 13, background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 10, padding: '10px 12px' }}>
          Coś poszło nie tak. Spróbuj ponownie.
        </div>
      )}
      <button type="submit" disabled={status === 'sending'}
        style={{ padding: '14px', borderRadius: 14, border: 'none', background: AMBER, color: '#fff', fontWeight: 800, fontSize: 15, cursor: 'pointer', fontFamily: 'inherit', opacity: status === 'sending' ? .7 : 1 }}>
        {status === 'sending' ? 'Wysyłanie…' : submitLabel}
      </button>
    </form>
  );
}

// ─── About screen ─────────────────────────────────────────────────────────────
function AboutScreen() {
  return (
    <div style={{ height: '100%', overflowY: 'auto', background: '#FDF8F3' }}>
      <div style={{ maxWidth: 480, margin: '0 auto', padding: '16px 16px 40px', display: 'flex', flexDirection: 'column', gap: 24 }}>

        <section style={{ background: '#fff', borderRadius: 16, padding: '20px', boxShadow: '0 2px 10px rgba(0,0,0,.05)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
            <span style={{ fontSize: 28 }}>🗺️</span>
            <h2 style={{ fontWeight: 800, fontSize: 18, color: '#111' }}>O KidzMap</h2>
          </div>
          <p style={{ fontSize: 14, color: '#555', lineHeight: 1.7 }}>
            KidzMap to bezpłatna aplikacja dla rodziców, którzy szukają ciekawych aktywności dla swoich dzieci w Polsce.
          </p>
          <p style={{ fontSize: 14, color: '#555', lineHeight: 1.7, marginTop: 10 }}>
            Zaczynamy od Warszawy — z ponad 70 sprawdzonymi miejscami dla dzieci w różnym wieku.
          </p>
          <p style={{ fontSize: 14, color: '#555', lineHeight: 1.7, marginTop: 10 }}>
            Filtruj po wieku, typie aktywności, cenie i lokalizacji. Wszystko w jednym miejscu.
          </p>
        </section>

        <section style={{ background: '#fff', borderRadius: 16, padding: '20px', boxShadow: '0 2px 10px rgba(0,0,0,.05)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
            <span style={{ fontSize: 24 }}>📍</span>
            <h2 style={{ fontWeight: 800, fontSize: 18, color: '#111' }}>Zaproponuj miejsce</h2>
          </div>
          <p style={{ fontSize: 13, color: '#888', marginBottom: 18, lineHeight: 1.5 }}>
            Znasz fajne miejsce, którego tu nie ma? Daj nam znać — sprawdzimy i dodamy.
          </p>
          <ContactForm
            endpoint={FORMSPREE_SUGGEST}
            submitLabel="Wyślij propozycję"
            fields={[
              { name: 'name',     label: 'Nazwa miejsca', type: 'text',     required: true,  placeholder: 'np. Centrum Nauki Kopernik' },
              { name: 'address',  label: 'Adres',         type: 'text',     required: true,  placeholder: 'ul. Przykładowa 1, Warszawa' },
              { name: 'category', label: 'Kategoria',     type: 'select',   required: true,  options: ['Sport', 'Kreatywne', 'Przyroda', 'Edukacja', 'Rozrywka'] },
              { name: 'email',    label: 'Twój email',    type: 'email',    required: false, placeholder: 'opcjonalnie' },
              { name: 'notes',    label: 'Uwagi',         type: 'textarea', required: false, placeholder: 'Cokolwiek chcesz dodać…' },
            ]}
          />
        </section>

        <section style={{ background: '#fff', borderRadius: 16, padding: '20px', boxShadow: '0 2px 10px rgba(0,0,0,.05)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
            <span style={{ fontSize: 24 }}>✉️</span>
            <h2 style={{ fontWeight: 800, fontSize: 18, color: '#111' }}>Kontakt</h2>
          </div>
          <p style={{ fontSize: 13, color: '#888', marginBottom: 18, lineHeight: 1.5 }}>
            Masz pytanie, uwagę lub chcesz współpracować? Napisz do nas.
          </p>
          <ContactForm
            endpoint={FORMSPREE_CONTACT}
            submitLabel="Wyślij wiadomość"
            fields={[
              { name: 'firstName', label: 'Imię',      type: 'text',     required: true, placeholder: 'Twoje imię' },
              { name: 'email',     label: 'Email',     type: 'email',    required: true, placeholder: 'twoj@email.pl' },
              { name: 'message',   label: 'Wiadomość', type: 'textarea', required: true, placeholder: 'Twoja wiadomość…' },
            ]}
          />
        </section>

      </div>
    </div>
  );
}

// ─── App ──────────────────────────────────────────────────────────────────────
export default function App() {
  const { t, i18n } = useTranslation();

  // Banner
  const [showBanner, setShowBanner] = useState(
    () => localStorage.getItem('kidzmap_welcomed') !== 'true'
  );
  function dismissBanner() {
    localStorage.setItem('kidzmap_welcomed', 'true');
    setShowBanner(false);
  }

  // Filters
  const [ages,     setAges]     = useState([]);
  const [cats,     setCats]     = useState([]);
  const [settings, setSettings] = useState([]);
  const [prices,   setPrices]   = useState([]);
  const [radius,   setRadius]   = useState(null);

  // UI
  const [view,     setView]     = useState('map');
  const [selected, setSelected] = useState(null);

  // Location
  const [userLat, setUserLat] = useState(null);
  const [userLng, setUserLng] = useState(null);

  // Data
  const [activities, setActivities] = useState([]);
  const [ratingsMap, setRatingsMap] = useState({});

  // Fetch from Supabase
  useEffect(() => {
    async function fetchActivities() {
      const { data, error } = await supabase
        .from('activities')
        .select('*')
        .eq('is_active', true);
      if (error) { console.error('Supabase error:', error.message); return; }
      setActivities(data.map(a => ({
        id: a.id, name: a.name, description: a.description,
        category: a.category, tags: a.tags || [],
        ageMin: a.age_min, ageMax: a.age_max,
        setting: a.setting, priceType: a.price_type, priceAmount: a.price_amount,
        address: a.address, city: a.city, lat: a.lat, lng: a.lng,
        openingHours: a.opening_hours, website: a.website,
        phone: a.phone, photos: a.photos || [], googlePlaceId: a.google_place_id || null, googleRating: a.google_rating || null, googleRatingTotal: a.google_rating_total || null,
      })));
    }
    fetchActivities();
  }, []);

// Fetch all ratings
  useEffect(() => {
    async function fetchRatings() {
      try {
        const { data, error } = await supabase
          .from('ratings')
          .select('activity_id, score');

        if (error || !data) return;

        const map = {};
        data.forEach(r => {
          if (!map[r.activity_id]) map[r.activity_id] = { sum: 0, count: 0 };
          map[r.activity_id].sum   += r.score;
          map[r.activity_id].count += 1;
        });

        // Convert to { activityId: { avg, count } }
        const result = {};
        Object.keys(map).forEach(id => {
          const { sum, count } = map[id];
          result[id] = {
            avg:   Math.round((sum / count) * 10) / 10,
            count: count,
          };
        });

        setRatingsMap(result);
      } catch {
        // fail silently
      }
    }
    fetchRatings();
  }, []);
  
  // Geolocation
  useEffect(() => {
    function onLocation(e) { setUserLat(e.detail.latitude); setUserLng(e.detail.longitude); }
    window.addEventListener('kidzmap:location', onLocation);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        pos => { setUserLat(pos.coords.latitude); setUserLng(pos.coords.longitude); },
        () => {},
        { enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 }
      );
    }
    return () => window.removeEventListener('kidzmap:location', onLocation);
  }, []);

  // Derived
  const filtered = sortByDistance(
    applyFilters(activities, { ages, cats, settings, prices, radius, userLat, userLng }),
    userLat, userLng,
  );
  const totalFilters = ages.length + cats.length + settings.length + prices.length + (radius ? 1 : 0);
  const isAbout = view === 'about';

  function toggleLang() {
    i18n.changeLanguage(i18n.language === 'pl' ? 'en' : 'pl');
  }

  return (
    <>
      <style>{globalStyles}</style>

      <div style={{ height: '100dvh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

        {/* Header */}
        <div style={{ background: '#fff', boxShadow: '0 1px 0 #f0f0f0', flexShrink: 0, zIndex: 30 }}>
          <div style={{ maxWidth: 480, margin: '0 auto', padding: '11px 15px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
              <span style={{ fontSize: 22 }}>🗺️</span>
              <div>
                <div style={{ fontWeight: 800, fontSize: 16, color: '#111', lineHeight: 1 }}>KidzMap</div>
                <div style={{ fontSize: 11, color: '#bbb' }}>
                  {userLat ? t('location.myLocation') : t('location.city')}
                </div>
              </div>
            </div>
            <button onClick={toggleLang} style={{ fontSize: 11, fontWeight: 600, color: '#777', border: '1px solid #e5e5e5', borderRadius: 99, padding: '5px 10px', background: 'white', cursor: 'pointer', fontFamily: 'inherit' }}>
              {i18n.language === 'pl' ? 'EN' : 'PL'}
            </button>
          </div>
        </div>

        {/* Banner + filters — hidden on about screen */}
        {!isAbout && showBanner && <WelcomeBanner onDismiss={dismissBanner} />}
        {!isAbout && (
          <FilterBar
            ages={ages}         setAges={setAges}
            cats={cats}         setCats={setCats}
            settings={settings} setSettings={setSettings}
            prices={prices}     setPrices={setPrices}
            radius={radius}     setRadius={setRadius}
            userLat={userLat}
          />
        )}

        {/* Content */}
        <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}
            className={selected ? 'sheet-open' : ''}>

          {view === 'map' && (
            <MapView
              filtered={filtered} selected={selected}
              onPinClick={a => setSelected(a)}
              userLat={userLat} userLng={userLng}
            />
          )}

          {view === 'list' && (
            <div style={{ height: '100%', overflowY: 'auto', background: '#FDF8F3' }}>
              <div style={{ maxWidth: 480, margin: '0 auto', padding: '12px 13px 16px', display: 'flex', flexDirection: 'column', gap: 11 }}>

                {/* Results count */}
                <div style={{ paddingLeft: 2 }}>
                  {filtered.length === 0 ? (
                    <div style={{ fontSize: 13, color: '#bbb' }}>{t('list.noResults')}</div>
                  ) : (
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#555' }}>
                      {totalFilters > 0
                        ? `${filtered.length} ${filtered.length === 1 ? 'miejsce spełnia' : filtered.length < 5 ? 'miejsca spełniają' : 'miejsc spełnia'} kryteria`
                        : `Wszystkie miejsca (${filtered.length})`}
                    </div>
                  )}
                </div>

                {filtered.map((activity, i) => (
                  <ActivityCard
                    key={activity.id} activity={activity} index={i}
                    onDetails={setSelected} userLat={userLat} userLng={userLng} rating={ratingsMap[activity.id] || null} googleRating={activity.googleRating} googleRatingTotal={activity.googleRatingTotal}
                  />
                ))}

                {filtered.length === 0 && (
                  <div style={{ textAlign: 'center', padding: '50px 0' }}>
                    <div style={{ fontSize: 44 }}>🔍</div>
                    <div style={{ color: '#ccc', fontSize: 13, marginTop: 7 }}>{t('list.noResults')}</div>
                  </div>
                )}

              </div>
            </div>
          )}

          {view === 'about' && <AboutScreen />}

        </div>

        {/* Bottom nav */}
        <div style={{ background: '#fff', borderTop: '1px solid #f0f0f0', display: 'flex', flexShrink: 0, zIndex: 30, paddingBottom: 'max(env(safe-area-inset-bottom), 12px)' }}>
          {[
            { icon: '☰',  label: 'Lista', id: 'list'  },
            { icon: '🗺️', label: 'Mapa',  id: 'map'   },
            { icon: 'ℹ️', label: 'O nas', id: 'about' },
          ].map(({ icon, label, id }) => (
            <button key={id}
              onClick={() => { setView(id); setSelected(null); }}
              style={{ flex: 1, padding: '12px 0 8px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, fontSize: 11, fontWeight: 700, color: view === id ? AMBER : '#bbb', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}>
              <span style={{ fontSize: 19 }}>{icon}</span>
              {label}
            </button>
          ))}
        </div>

      </div>

      {selected && (
        <DetailSheet activity={selected} onClose={() => setSelected(null)}
          userLat={userLat} userLng={userLng} />
      )}
    </>
  );
}